/**
 * OLE2 / Compound File Binary (CFB) signature.
 *
 * A non-protected Office Open XML file (.xlsx / .xlsm / .docx …) is a plain ZIP
 * archive and starts with the "PK\x03\x04" signature. When such a file is
 * password-protected, Office encrypts it and wraps the payload inside an OLE2
 * compound file, which starts with these magic bytes instead.
 */
const OLE2_CFB_SIGNATURE = new Uint8Array([0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]);

/**
 * Encodes an ASCII string as a UTF-16LE byte sequence.
 *
 * CFB directory entry names are stored as UTF-16LE strings, so we match them as
 * UTF-16LE byte sequences (see MS-OFFCRYPTO). The stream names we look for are
 * pure ASCII, so a byte-per-code-unit encoding is sufficient.
 */
function utf16le(text: string): Uint8Array {
  const bytes = new Uint8Array(text.length * 2);
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    bytes[i * 2] = code & 0xff;
    bytes[i * 2 + 1] = (code >> 8) & 0xff;
  }
  return bytes;
}

/**
 * Directory entry names of the streams that hold an encrypted OOXML payload.
 */
const ENCRYPTED_PACKAGE_STREAM = utf16le('EncryptedPackage');
const ENCRYPTION_INFO_STREAM = utf16le('EncryptionInfo');

/** Decodes a base64 string into a byte array (works in both Node and browsers). */
function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Normalizes `fileContent` into a Uint8Array, accepting either raw bytes
 * (Uint8Array / Node Buffer) or a base64 string (with or without a
 * `data:...;base64,` prefix).
 */
function toBytes(fileContent: Uint8Array | string): Uint8Array {
  if (fileContent instanceof Uint8Array) {
    return fileContent;
  }
  return base64ToBytes(fileContent.replace(/^data:\w+\/.+;base64,/, ''));
}

/** Returns whether `haystack` begins with the bytes of `needle`. */
function startsWith(haystack: Uint8Array, needle: Uint8Array): boolean {
  if (haystack.length < needle.length) {
    return false;
  }
  for (let i = 0; i < needle.length; i += 1) {
    if (haystack[i] !== needle[i]) {
      return false;
    }
  }
  return true;
}

/** Returns whether `needle` appears anywhere within `haystack`. */
function includesBytes(haystack: Uint8Array, needle: Uint8Array): boolean {
  const lastStart = haystack.length - needle.length;
  for (let start = 0; start <= lastStart; start += 1) {
    let matches = true;
    for (let j = 0; j < needle.length; j += 1) {
      if (haystack[start + j] !== needle[j]) {
        matches = false;
        break;
      }
    }
    if (matches) {
      return true;
    }
  }
  return false;
}

/**
 * Detects whether a buffer is a password-protected (encrypted) Office document.
 *
 * The check is intentionally conservative: it only flags files that are OLE2
 * compound files AND contain an encryption stream, so a normal ZIP-based OOXML
 * file is never mistaken for a protected one.
 */
export function isFilePasswordProtected(fileContent: Uint8Array | string): boolean {
  const bytes = toBytes(fileContent);

  // A regular, non-protected xlsx/xlsm is a ZIP archive, never an OLE2 file.
  if (!startsWith(bytes, OLE2_CFB_SIGNATURE)) {
    return false;
  }

  // It is an OLE2 compound file: it is a protected OOXML file only if it carries
  // the encryption streams (this excludes legacy, non-encrypted binary formats).
  return (
    includesBytes(bytes, ENCRYPTED_PACKAGE_STREAM) || includesBytes(bytes, ENCRYPTION_INFO_STREAM)
  );
}
